/**
 * matrix.ts — matrix views of the graph, for linear-programming setups.
 *
 * The graph is authored (and simulated) as a directed edge list; these helpers
 * derive the classic LP matrices from it on demand — nothing here is
 * persisted or part of the wire contract (the modal's config is ephemeral):
 *
 * - `incidence`  B (nodes × edges): −w at the source row, +w at the target
 *                row (signed, the default) or counts (unsigned). B·f is the
 *                per-node flow balance — the conservation constraint
 *                (Kirchhoff) every flow LP needs: `B f = b`.
 * - `adjacency`  A (nodes × nodes): A[from][to] += w. A·x aggregates a node
 *                vector over out-edges (Aᵀ·x over in-edges) — weighted
 *                routing / propagation problems.
 * - `laplacian`  L = D − A where D[i][i] = Σⱼ A[i][j] (out-degree weights).
 *                Rows sum to zero; diffusion-style constraints.
 *
 * `w` is the edge's `weight`, any edge param key (`cellParam`), or constant 1.
 * `orientation: 'transpose'` flips rows/cols so the INPUT axis can be either
 * nodes or edges regardless of kind.
 *
 * Mirrored byte-for-byte in `backend/matrix.py` (the chat/MCP `graph_matrix`
 * tool) — keep the two in sync; `test/matrix.test.ts` and
 * `backend/test_matrix.py` assert the same numbers on the same fixture.
 */
import type { Graph, GraphEdge, GraphNode } from './types';

export type MatrixKind = 'incidence' | 'adjacency' | 'laplacian';
export type MatrixOrientation = 'normal' | 'transpose';

export interface MatrixIssue {
  severity: 'error' | 'warning';
  message: string;
}

/** One axis entry: the element id (stable key — edge ids for the edge axis)
 * plus a human display label (node label, or "src → tgt" for edges). */
export interface AxisLabel {
  id: string;
  label: string;
}

export interface MatrixBuild {
  kind: MatrixKind;
  orientation: MatrixOrientation;
  /** Which element type the ROW axis indexes (after orientation). */
  rowAxis: 'nodes' | 'edges';
  /** Which element type the COL axis indexes — this is the input axis. */
  colAxis: 'nodes' | 'edges';
  rows: AxisLabel[];
  cols: AxisLabel[];
  cells: number[][];
  issues: MatrixIssue[];
  /** False when any error-severity issue blocked a usable build. */
  ok: boolean;
}

export interface MatrixConfig {
  kind: MatrixKind;
  orientation: MatrixOrientation;
  /** Edge param key for cell weights ('weight' = the edge's weight field).
   * null = constant 1. */
  cellParam: string | null;
  /** Incidence only: signed (−w source / +w target, default) or unsigned
   * (both +w, i.e. counts). Ignored by adjacency/laplacian. */
  signed: boolean;
  /** When `cellParam` is missing on an edge: true → cell 0 + warning;
   * false → error (the matrix cannot be generated as asked). */
  missingAsZero: boolean;
}

/** One selectable param key + how many elements carry it (drives the
 * dropdown hints "capacity · 5/7 nodes"). */
export interface ParamOption {
  key: string;
  count: number;
}

export interface ParamOptions {
  nodes: ParamOption[];
  edges: ParamOption[];
}

/** Cap on how many offending ids an issue message lists before "…and N more". */
const LIST_CAP = 5;

function listIds(ids: string[]): string {
  const shown = ids.slice(0, LIST_CAP).map((id) => `'${id}'`).join(', ');
  return ids.length > LIST_CAP ? `${shown}, … and ${ids.length - LIST_CAP} more` : shown;
}

/** The edge's value for a param key. 'weight' is the pseudo-param backed by
 * the edge's own `weight` field (it has no ParamSpec of its own). */
export function edgeParamValue(edge: GraphEdge, key: string): number | undefined {
  if (key === 'weight') return edge.weight;
  const p = edge.params.find((q) => q.key === key);
  return p ? p.value : undefined;
}

export function nodeParamValue(node: GraphNode, key: string): number | undefined {
  const p = node.params.find((q) => q.key === key);
  return p ? p.value : undefined;
}

/** Distinct param keys across nodes and edges (edge side always leads with
 * 'weight'), with the count of elements carrying each key. */
export function collectParamOptions(graph: Graph): ParamOptions {
  const nodeCounts = new Map<string, number>();
  for (const n of graph.nodes) {
    for (const p of n.params) nodeCounts.set(p.key, (nodeCounts.get(p.key) ?? 0) + 1);
  }
  const edgeCounts = new Map<string, number>([['weight', graph.edges.length]]);
  for (const e of graph.edges) {
    for (const p of e.params) edgeCounts.set(p.key, (edgeCounts.get(p.key) ?? 0) + 1);
  }
  const toOptions = (m: Map<string, number>): ParamOption[] =>
    [...m.entries()].map(([key, count]) => ({ key, count })).sort((a, b) =>
      a.key === 'weight' ? -1 : b.key === 'weight' ? 1 : a.key.localeCompare(b.key));
  return { nodes: toOptions(nodeCounts), edges: toOptions(edgeCounts) };
}

function zeros(rows: number, cols: number): number[][] {
  return Array.from({ length: rows }, () => Array<number>(cols).fill(0));
}

/** Build the matrix. Always returns a MatrixBuild; check `.ok` and `.issues`
 * — errors mean the preview/codegen should be blocked (the cells may be
 * partial). */
export function buildMatrix(graph: Graph, config: MatrixConfig): MatrixBuild {
  const issues: MatrixIssue[] = [];
  const nodes = graph.nodes;
  const edges = graph.edges;
  if (nodes.length === 0) {
    issues.push({ severity: 'error', message: 'The graph has no nodes — nothing to build a matrix from.' });
  }
  if (edges.length === 0) {
    issues.push({ severity: 'error', message: 'The graph has no edges — the matrix would be all zeros.' });
  }

  const nodeIndex = new Map(nodes.map((n, i) => [n.id, i]));
  const nodeLabel = (id: string): string => nodeIndex.has(id)
    ? (nodes[nodeIndex.get(id) as number].label || id)
    : id;
  const edgeLabel = (e: GraphEdge): string => `${nodeLabel(e.source)} → ${nodeLabel(e.target)}`;

  // Resolve each edge's cell weight up front so missing-param issues are
  // reported once, up front, instead of per cell.
  const weights: number[] = [];
  if (config.cellParam !== null) {
    const missing: string[] = [];
    for (const e of edges) {
      const v = edgeParamValue(e, config.cellParam);
      if (v === undefined) missing.push(e.id);
      weights.push(v ?? 0);
    }
    if (missing.length > 0) {
      const msg = `Edge param '${config.cellParam}' is missing on ${missing.length} edge(s): ${listIds(missing)}.`;
      issues.push(config.missingAsZero
        ? { severity: 'warning', message: `${msg} Those cells are 0.` }
        : { severity: 'error', message: `${msg} Check "missing → 0" to allow it.` });
    }
  } else {
    for (const _e of edges) weights.push(1);
  }

  const usables: { edge: GraphEdge; w: number }[] = [];
  edges.forEach((e, j) => {
    if (!nodeIndex.has(e.source) || !nodeIndex.has(e.target)) {
      issues.push({ severity: 'error', message: `Edge '${e.id}' references an unknown node — the graph is inconsistent.` });
      return;
    }
    usables.push({ edge: e, w: weights[j] });
  });

  let rows: AxisLabel[] = nodes.map((n) => ({ id: n.id, label: n.label || n.id }));
  let cols: AxisLabel[] = config.kind === 'incidence'
    ? usables.map(({ edge }) => ({ id: edge.id, label: edgeLabel(edge) }))
    : nodes.map((n) => ({ id: n.id, label: n.label || n.id }));
  let cells: number[][];

  if (config.kind === 'incidence') {
    cells = zeros(nodes.length, usables.length);
    usables.forEach(({ edge, w }, j) => {
      const s = nodeIndex.get(edge.source) as number;
      const t = nodeIndex.get(edge.target) as number;
      if (s === t) {
        if (config.signed) {
          issues.push({ severity: 'warning', message: `Self-loop '${edge.id}' (${edgeLabel(edge)}) cancels to 0 in the signed incidence matrix.` });
        } else {
          cells[s][j] += 2 * w;
        }
        return;
      }
      if (config.signed) {
        cells[s][j] -= w;
        cells[t][j] += w;
      } else {
        cells[s][j] += w;
        cells[t][j] += w;
      }
    });
  } else {
    // adjacency (+ laplacian, derived from it). Parallel edges merge into one
    // cell (summed) — flagged below per pair.
    const A = zeros(nodes.length, nodes.length);
    const byPair = new Map<string, GraphEdge[]>();
    usables.forEach(({ edge, w }) => {
      const s = nodeIndex.get(edge.source) as number;
      const t = nodeIndex.get(edge.target) as number;
      A[s][t] += w;
      const pairKey = `${edge.source}→${edge.target}`;
      byPair.set(pairKey, [...(byPair.get(pairKey) ?? []), edge]);
      if (s === t) {
        issues.push({ severity: 'warning', message: `Self-loop '${edge.id}' (${edgeLabel(edge)}) lands on the diagonal.` });
      }
    });
    for (const [pairKey, group] of byPair) {
      if (group.length > 1) {
        issues.push({ severity: 'warning', message: `${group.length} parallel edges on ${pairKey} are summed into one cell: ${listIds(group.map((e) => e.id))}.` });
      }
    }
    if (config.kind === 'adjacency') {
      cells = A;
    } else {
      // Laplacian (out-degree convention): L[i][i] = Σⱼ A[i][j], off-diag −A.
      cells = zeros(nodes.length, nodes.length);
      for (let i = 0; i < nodes.length; i++) {
        let out = 0;
        for (let j = 0; j < nodes.length; j++) out += A[i][j];
        cells[i][i] = out;
      }
      for (const [pairKey] of byPair) {
        const [src, tgt] = pairKey.split('→');
        const s = nodeIndex.get(src) as number;
        const t = nodeIndex.get(tgt) as number;
        if (s === t) continue; // already folded into the diagonal via row sum
        cells[s][t] = -A[s][t];
      }
    }
  }

  // Structural warnings (advisory only — the matrix is still well-formed).
  const touched = new Set<string>();
  for (const e of usables) { touched.add(e.edge.source); touched.add(e.edge.target); }
  const isolated = nodes.filter((n) => !touched.has(n.id)).map((n) => n.id);
  if (isolated.length > 0) {
    issues.push({ severity: 'warning', message: `${isolated.length} isolated node(s) produce zero rows/cols: ${listIds(isolated)}.` });
  }

  let rowAxis: 'nodes' | 'edges' = 'nodes';
  let colAxis: 'nodes' | 'edges' = config.kind === 'incidence' ? 'edges' : 'nodes';
  if (config.orientation === 'transpose') {
    [rows, cols] = [cols, rows];
    // cells[0].map: outer length = column count (the new row count).
    cells = cells.length > 0 && cells[0].length > 0
      ? cells[0].map((_, j) => cells.map((row) => row[j]))
      : cells;
    [rowAxis, colAxis] = [colAxis, rowAxis];
  }

  const ok = issues.every((i) => i.severity !== 'error');
  return {
    kind: config.kind,
    orientation: config.orientation,
    rowAxis, colAxis, rows, cols,
    cells: cells ?? [],
    issues,
    ok,
  };
}

export type InputSource = 'node' | 'edge' | 'custom';

export interface InputConfig {
  source: InputSource;
  /** Param key for node/edge sources (null = custom). */
  param: string | null;
  /** Custom literal values (custom source only). */
  values: number[];
  missingAsZero: boolean;
}

export interface InputBuild {
  values: number[];
  issues: MatrixIssue[];
  ok: boolean;
}

/** The input vector x, aligned with the build's COLUMN axis. Node/edge param
 * sources must match the axis type (transpose flips it) — a mismatch is an
 * error, not an auto-flip: the user asked for a specific dot product. */
export function buildInput(graph: Graph, build: MatrixBuild, config: InputConfig): InputBuild {
  const issues: MatrixIssue[] = [];
  const axis = build.colAxis;
  if (config.source === 'custom') {
    if (config.values.length !== build.cols.length) {
      issues.push({ severity: 'error', message: `Custom input needs ${build.cols.length} value(s) (one per column), got ${config.values.length}.` });
      return { values: [], issues, ok: false };
    }
    return { values: [...config.values], issues, ok: true };
  }
  const wantAxis = config.source === 'node' ? 'nodes' : 'edges';
  if (axis !== wantAxis) {
    issues.push({
      severity: 'error',
      message: `The input vector must align with the column axis (${axis}). '${config.source}' params don't — switch the source or the orientation.`,
    });
    return { values: [], issues, ok: false };
  }
  const key = config.param;
  if (!key) {
    issues.push({ severity: 'error', message: 'Pick a param for the input vector (or use custom values).' });
    return { values: [], issues, ok: false };
  }
  const values: number[] = [];
  const missing: string[] = [];
  if (axis === 'edges') {
    for (const col of build.cols) {
      const e = graph.edges.find((q) => q.id === col.id);
      const v = e ? edgeParamValue(e, key) : undefined;
      if (v === undefined) missing.push(col.id);
      values.push(v ?? 0);
    }
  } else {
    for (const col of build.cols) {
      const n = graph.nodes.find((q) => q.id === col.id);
      const v = n ? nodeParamValue(n, key) : undefined;
      if (v === undefined) missing.push(col.id);
      values.push(v ?? 0);
    }
  }
  if (missing.length > 0) {
    const msg = `Param '${key}' is missing on ${missing.length} ${axis} element(s): ${listIds(missing)}.`;
    issues.push(config.missingAsZero
      ? { severity: 'warning', message: `${msg} Those entries are 0.` }
      : { severity: 'error', message: `${msg} Check "missing → 0" to allow it.` });
  }
  return { values, issues, ok: issues.every((i) => i.severity !== 'error') };
}

/** y = M·x. */
export function multiply(cells: number[][], x: number[]): number[] {
  return cells.map((row) => row.reduce((sum, v, j) => sum + v * (x[j] ?? 0), 0));
}
